import { useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { userService } from "../../services/userService";
import { debounce } from "../../utils/debounce";

interface User {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  profilePicture?: string;
}

interface UserSelectorProps {
  value: string;
  onChange: (value: string) => void;
  role?: string;
  placeholder?: string;
}

export function UserSelector({
  value,
  onChange,
  role = "student",
  placeholder = "Search by name or email...",
}: UserSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchUsers = async (term: string) => {
    setIsLoading(true);
    try {
      const response = await userService.getAllUsers({
        search: term,
        role,
      });
      const usersList = response.users || [];
      setUsers(usersList);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetch = useRef(
    debounce((term: string) => {
      fetchUsers(term);
    }, 300)
  );

  useEffect(() => {
    if (isOpen) {
      debouncedFetch.current(searchTerm);
      searchInputRef.current?.focus();
    }
  }, [isOpen, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm("");
    }
  };

  const handleSelectUser = (userId: string) => {
    onChange(userId);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setSearchTerm("");
  };

  const selectedUser = users.find((u) => u._id === value);

  const formatUserName = (user: User) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    }
    return user.email;
  };

  const getInitials = (user: User) => {
    const name = formatUserName(user);
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      <label className="text-sm font-medium text-gray-700">
        Select Student
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer flex items-center justify-between text-sm bg-white hover:border-gray-400 transition-all"
        >
          <span className="truncate text-left">
            {selectedUser ? (
              <span className="text-gray-900">
                {formatUserName(selectedUser)}
              </span>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </span>
          <div className="flex items-center gap-1 text-gray-400 shrink-0 ml-2">
            {value && (
              <X
                size={14}
                className="hover:text-red-500 cursor-pointer"
                onClick={handleClear}
              />
            )}
            <Search size={16} />
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
            <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
              <Search size={14} className="text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full bg-transparent border-none outline-none text-sm placeholder:text-gray-400"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {isLoading && <Loader2 size={14} className="animate-spin text-gray-400" />}
            </div>

            <div className="max-h-60 overflow-y-auto">
              {isLoading && users.length === 0 ? (
                <div className="px-4 py-8 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Loading...
                </div>
              ) : users.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No users found
                </div>
              ) : (
                users.map((user) => (
                  <div
                    key={user._id}
                    className={`px-4 py-2 text-sm cursor-pointer transition-colors flex items-center gap-3 ${
                      user._id === value
                        ? "bg-indigo-50 text-indigo-700 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => handleSelectUser(user._id)}
                  >
                    {user.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-medium shrink-0">
                        {getInitials(user)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{formatUserName(user)}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
